/**
 * @file components/MetricsCard.jsx
 * @description A stat/metric summary card for the Dashboard.
 *
 * Renders an icon, a large numeric value, and a label inside a Bootstrap card
 * with a coloured top border using the brand accent colours.
 *
 * @param {Object}  props
 * @param {string}  props.title       - Metric label shown below the value.
 * @param {string|number} props.value - The primary statistic to display.
 * @param {string}  props.icon        - Bootstrap Icons class, e.g. 'bi-key'.
 * @param {string}  [props.accent]    - CSS custom property name for the top border colour.
 *                                      Defaults to '--color-primary'.
 * @param {string}  [props.subtitle]  - Optional secondary line below the title.
 */

import React from 'react';

/**
 * Displays a single metric in a styled card.
 */
export default function MetricsCard({
  title,
  value,
  icon,
  accent = '--color-primary',
  subtitle,
}) {
  return (
    <div
      className="card metric-card h-100"
      style={{ borderTopColor: `var(${accent})` }}
    >
      <div className="card-body d-flex align-items-start gap-3">
        {/* Icon column */}
        <div
          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: 48,
            height: 48,
            backgroundColor: `var(${accent})`,
            opacity: 0.85,
          }}
          aria-hidden="true"
        >
          <i className={`bi ${icon} fs-5 text-white`} />
        </div>

        {/* Text column */}
        <div>
          <p className="text-muted small mb-1">{title}</p>
          <h4 className="fw-bold mb-0" style={{ color: 'var(--color-text)' }}>
            {value ?? '—'}
          </h4>
          {subtitle && (
            <p className="text-muted small mb-0 mt-1">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );
}
